import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {useFeatureAppEnv} from '@smart/react-utils';
import {PAYMENT_TYPE} from '@smart/types';
import {
  getLabelsForMarketCustomerTypePaymentType,
  isFeatureFlagEnabled,
} from '@smart/utils';
import {Notification, Text} from '@smart/web-components';
import * as React from 'react';
import type {LeasingContent} from '../../bff/app-model/fetch-app-model';
import type {PriceDetailType} from '../../bff/price-details';
import {useAppModelContext} from '../hooks/use-app-model-context';
import type {DelayedStateResult} from '../hooks/use-delayed-state';
import {useLocale} from '../hooks/use-locale';
import type {MaybePriceDetails} from '../hooks/use-price-details';
import {LeasingInformationTable} from './leasing-information-table';
import styles from './payment-summary.module.scss';
import {Price} from './price';

export interface PaymentSummaryProps {
  readonly label: string;
  readonly errorMessage: string;
  readonly priceDetailsResult: DelayedStateResult<MaybePriceDetails>;
  readonly priceType: PriceTypeV1;
  readonly paymentMode?: LeasingContent['mode'];
  readonly getPrice: (priceDetails: PriceDetailType) => string;
  readonly onErrorRetry: () => void;
}

export function PaymentSummary({
  errorMessage,
  getPrice,
  label,
  onErrorRetry,
  paymentMode,
  priceDetailsResult,
  priceType,
}: PaymentSummaryProps): JSX.Element {
  const {
    featureServices: {'endpoint-directory': endpointDirectory},
  } = useFeatureAppEnv();

  const {appModel, appMarketConfig} = useAppModelContext();
  const {content, featureFlagsConfig} = appModel;
  const {excludingVatCaption, includingVatCaption, excludesTax, includesTax} =
    content;

  const {marketId, language} = useLocale();

  const {vatLabel} = getLabelsForMarketCustomerTypePaymentType({
    marketId,
    labels: {
      excludeVatLabel: excludesTax,
      includesVatLabel: includesTax,
    },
    isSMECustomer: priceType === `commercial`,
    paymentType:
      paymentMode === `leasing` || paymentMode === `finance`
        ? PAYMENT_TYPE.LEASING
        : PAYMENT_TYPE.CASH,
  });

  const vatCaptionConfig = {
    commercial: excludingVatCaption,
    private: includingVatCaption,
  };

  const showOnlyTotalGrossPrice =
    appMarketConfig.showOnlyTotalGrossPrice && paymentMode !== `leasing`;

  const vatCaption = showOnlyTotalGrossPrice
    ? includingVatCaption
    : vatLabel ?? vatCaptionConfig[priceType];

  const showVatCaption = paymentMode !== `finance`;

  const isHideSalesToolsLeasingRateEnabled = isFeatureFlagEnabled(
    `HIDE_SALES_TOOLS_LEASING_RATE`,
    marketId,
    featureFlagsConfig,
  );

  const usedPriceType = showOnlyTotalGrossPrice ? `private` : priceType;

  const priceDetails =
    priceDetailsResult.status === `completed` &&
    `priceDetails` in priceDetailsResult.state
      ? priceDetailsResult.state.priceDetails[usedPriceType]
      : undefined;

  const attributes =
    priceDetailsResult.status === `completed` &&
    `priceDetails` in priceDetailsResult.state
      ? priceDetailsResult.state.priceDetails.attributes
      : [];

  if (endpointDirectory.envName !== `prod`) {
    console.debug({
      'AEM Tax Label Path': vatLabel
        ? `/content/common/${marketId}/${language}/common-attributes`
        : `content/payment-calculator/${marketId}/${language}/content`,
    });
  }

  return (
    <div className={styles.paymentSummary}>
      {priceDetailsResult.status === `completed` &&
      `error` in priceDetailsResult.state ? (
        <Notification
          className={styles.notification}
          text={errorMessage}
          variant="warning"
          closeButton={{ariaLabel: `Close`, onClick: onErrorRetry}}
        />
      ) : (isHideSalesToolsLeasingRateEnabled && paymentMode === `leasing`) ||
        paymentMode === `finance` ? (
        <LeasingInformationTable
          paymentMode={paymentMode}
          priceDetails={priceDetails}
          attributes={attributes}
          priceType={priceType}
        />
      ) : (
        <>
          <Text variant="label-100">{label}</Text>
          <div className={styles.price}>
            <Price
              value={priceDetails ? getPrice(priceDetails) : undefined}
              loading={priceDetailsResult.status === `fallback`}
            />
            {showVatCaption && (
              <Text className={styles.vatCaption} variant="caption-300">
                {vatCaption}
              </Text>
            )}
          </div>
        </>
      )}
    </div>
  );
}
